import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Phone, Mail, Building2, Users, QrCode,
  Download, TrendingUp, Package, Edit2, ExternalLink, Store,
  Coffee, AlertCircle, CheckCircle, Calendar, Activity, Upload,
  Trash2, Image as ImageIcon, Clock, Truck, MessageSquare, X,
  RefreshCw, FileText, User, Star, StarOff
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import AdminLayout from '../../components/admin/AdminLayout';
import { adminApi } from '../../lib/api';
import { resolveAssetUrl, flag } from '../../config';

import { formatDateEST, formatDateOnlyEST } from '../../utils/dateUtils';
import { toast } from 'sonner';

const LocationDetail = () => {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const [location, setLocation] = useState(null);
  const [venuePartner, setVenuePartner] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [coasterDesigns, setCoasterDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCoasterModal, setShowCoasterModal] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  // Featured business state
  const [featuredBusinessId, setFeaturedBusinessId] = useState(null);
  const [savingFeatured, setSavingFeatured] = useState(false);

  // Live Coaster Design states
  const [uploadingLiveCoaster, setUploadingLiveCoaster] = useState(false);
  const [liveCoasterFront, setLiveCoasterFront] = useState(null);
  const [liveCoasterBack, setLiveCoasterBack] = useState(null);
  const [setAsDefault, setSetAsDefault] = useState(false);

  // QR code regeneration state
  const [regeneratingQR, setRegeneratingQR] = useState(false);

  useEffect(() => {
    fetchLocationDetails();
    fetchCoasterDesigns();
  }, [locationId]);

  const fetchLocationDetails = async () => {
    try {
      setLoading(true);

      // Fetch location details
      const locationResponse = await adminApi.get(
        `/locations/${locationId}`
      );
      setLocation(locationResponse.data);
      setAdminNotes(locationResponse.data.admin_notes || '');
      setFeaturedBusinessId(locationResponse.data.featured_business_id || null);

      // Fetch venue partner if exists
      if (locationResponse.data.venue_partner_id) {
        try {
          const venueResponse = await adminApi.get(
            `/venue/partner/${locationResponse.data.venue_partner_id}`
          );
          setVenuePartner(venueResponse.data);
        } catch (err) {
          console.error('Error fetching venue partner:', err);
        }
      }

      // Fetch businesses at this location
      const businessesResponse = await adminApi.get(
        `/locations/${locationId}/businesses`
      );
      setBusinesses(businessesResponse.data);

    } catch (error) {
      console.error('Error fetching location details:', error);
      toast.error('Failed to load location details');
    } finally {
      setLoading(false);
    }
  };

  const fetchCoasterDesigns = async () => {
    try {
      const response = await adminApi.get(`/admin/coaster-designs`);
      setCoasterDesigns(response.data);
    } catch (error) {
      console.error('Error fetching coaster designs:', error);
    }
  };

  const calculateCoasterStats = () => {
    if (!location?.coaster_inventory || !location?.daily_customer_estimate) {
      return null;
    }

    const remaining = location.coaster_inventory;
    const dailyUsage = location.daily_customer_estimate;
    const daysRemaining = Math.floor(remaining / dailyUsage);
    const percentage = location.initial_coaster_quantity
      ? (remaining / location.initial_coaster_quantity) * 100
      : 0;

    return {
      remaining,
      dailyUsage,
      daysRemaining,
      percentage,
      status: percentage > 50 ? 'good' : percentage > 20 ? 'warning' : 'critical'
    };
  };

  const handleSaveNotes = async () => {
    try {
      await adminApi.patch(
        `/admin/locations/${locationId}`,
        { admin_notes: adminNotes }
      );
      toast.success('Notes saved successfully');
      setShowNotesModal(false);
      fetchLocationDetails();
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Failed to save notes');
    }
  };

  const handleSetFeatured = async (businessId) => {
    const newFeaturedId = featuredBusinessId === businessId ? null : businessId;

    setSavingFeatured(true);
    try {
      const response = await adminApi.put(
        `/admin/locations/${locationId}/featured-business`,
        { business_id: newFeaturedId }
      );

      if (response.data.success) {
        setFeaturedBusinessId(newFeaturedId);
        toast.success(
          newFeaturedId
            ? 'Featured business set!'
            : 'Featured business removed'
        );
      } else {
        toast.error('Failed to update featured business');
      }
    } catch (err) {
      console.error('Error setting featured:', err);
      toast.error(err.response?.data?.detail || 'Error updating featured business');
    } finally {
      setSavingFeatured(false);
    }
  };

  const handleDownloadQR = async () => {
    try {

      const response = await adminApi.get(
        `/admin/locations/${locationId}/qr-download`,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qr-${location.slug}.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('QR code downloaded successfully!');
    } catch (error) {
      console.error('Error downloading QR code:', error);
      toast.error('Failed to download QR code');
    }
  };

  const handleRegenerateQR = async () => {
    try {
      setRegeneratingQR(true);

      const response = await adminApi.post(
        `/admin/locations/${locationId}/regenerate-qr`,
        {}
      );

      if (response.data.success) {
        setLocation(prev => ({
          ...prev,
          qr_code_url: response.data.qr_code_url
        }));
        toast.success('QR code regenerated successfully!');
      } else {
        throw new Error(response.data.message || 'Failed to regenerate QR code');
      }
    } catch (error) {
      console.error('Error regenerating QR code:', error);
      toast.error(error.response?.data?.detail || 'Failed to regenerate QR code');
    } finally {
      setRegeneratingQR(false);
    }
  };

  const handleUpdateCoasterDesign = async (designId) => {
    try {
      await adminApi.patch(
        `/admin/locations/${locationId}`,
        { selected_coaster_id: designId }
      );

      toast.success('Coaster design updated successfully');
      setShowCoasterModal(false);
      fetchLocationDetails();
    } catch (error) {
      console.error('Error updating coaster design:', error);
      toast.error('Failed to update coaster design');
    }
  };

  const handleUploadLiveCoaster = async () => {
    if (!liveCoasterFront && !liveCoasterBack) {
      toast.error('Please select at least one image to upload');
      return;
    }

    try {
      setUploadingLiveCoaster(true);
      const formData = new FormData();

      if (liveCoasterFront) {
        formData.append('front_image', liveCoasterFront);
      }
      if (liveCoasterBack) {
        formData.append('back_image', liveCoasterBack);
      }
      formData.append('set_as_default', setAsDefault);

      await adminApi.put(
        `/admin/locations/${locationId}/live-coaster`,
        formData, { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      toast.success('Live coaster design uploaded successfully');
      setLiveCoasterFront(null);
      setLiveCoasterBack(null);
      setSetAsDefault(false);
      fetchLocationDetails();
    } catch (error) {
      console.error('Error uploading live coaster:', error);
      toast.error('Failed to upload live coaster design');
    } finally {
      setUploadingLiveCoaster(false);
    }
  };

  const handleRemoveLiveCoaster = async () => {
    if (!window.confirm('Are you sure you want to remove the live coaster? The scan page will use the system default instead.')) {
      return;
    }

    try {
      await adminApi.delete(
        `/admin/locations/${locationId}/live-coaster`
      );

      toast.success('Live coaster removed. Location will now use system default.');
      fetchLocationDetails();
    } catch (error) {
      console.error('Error removing live coaster:', error);
      toast.error('Failed to remove live coaster');
    }
  };

  const handleUploadLogo = async (file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('logo', file);

      await adminApi.post(
        `/admin/venue-partners/${venuePartner.id}/upload-logo`,
        formData, { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      toast.success('Logo uploaded successfully');
      fetchLocationDetails();
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    }
  };

  const handleDeleteLogo = async () => {
    if (!confirm('Are you sure you want to delete the venue logo?')) return;

    try {
      await adminApi.delete(
        `/admin/venue-partners/${venuePartner.id}/logo`
      );

      toast.success('Logo deleted successfully');
      fetchLocationDetails();
    } catch (error) {
      console.error('Error deleting logo:', error);
      toast.error('Failed to delete logo');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-venue"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!location) {
    return (
      <AdminLayout>
        <div className="text-center py-20">
          <div className="w-20 h-20 bg-navy/5 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle size={40} className="text-navy/20" />
          </div>
          <h2 className="font-display text-2xl text-navy mb-4">Location Not Found</h2>
          <button
            onClick={() => navigate('/admin/locations')}
            className="px-4 py-2.5 bg-gradient-to-r from-venue to-teal-400 rounded-xl text-white font-medium"
          >
            Back to Hubs
          </button>
        </div>
      </AdminLayout>
    );
  }

  const coasterStats = calculateCoasterStats();
  const selectedDesign = coasterDesigns.find(d => d.id === (venuePartner?.selected_coaster_id || location?.selected_coaster_id));

  // Tab configuration
  const tabs = [
    { id: 'overview', label: 'Overview', icon: Building2 },
    { id: 'qrcode', label: 'QR Code', icon: QrCode },
    ...(venuePartner ? [{ id: 'venue', label: 'Venue Partner', icon: Coffee }] : []),
    ...(coasterStats ? [{ id: 'coasters', label: 'Coasters', icon: Package }] : []),
    { id: 'advertisers', label: 'Advertisers', icon: Store, badge: businesses.length },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Back Link */}
        <button
          onClick={() => navigate('/admin/locations')}
          className="flex items-center gap-2 text-navy/60 hover:text-navy transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Hubs
        </button>

        {/* Page Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="font-mono text-venue text-sm mb-1">// admin.locations.detail</p>
            <h1 className="font-display text-3xl text-navy">{location.name}</h1>
            <p className="text-navy/50 mt-1 flex items-center gap-1">
              <MapPin size={14} />
              {location.address?.city || location.city}, {location.address?.state || location.state}
            </p>
          </div>
          <span className={`px-4 py-2 rounded-full font-medium flex items-center gap-2 ${
            location.is_active ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
          }`}>
            {location.is_active ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
            {location.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>

        {/* Stats Overview - Always visible */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-navy/5 rounded-2xl p-6">
            <div className="w-10 h-10 bg-venue/10 rounded-xl flex items-center justify-center mb-4">
              <TrendingUp size={20} className="text-venue" />
            </div>
            <p className="text-sm text-navy/50 mb-1">Total Scans</p>
            <p className="text-3xl font-bold text-venue">{location.scan_count || 0}</p>
          </div>

          <div className="bg-consumer/5 rounded-2xl p-6">
            <div className="w-10 h-10 bg-consumer/10 rounded-xl flex items-center justify-center mb-4">
              <Store size={20} className="text-consumer" />
            </div>
            <p className="text-sm text-navy/50 mb-1">Active Advertisers</p>
            <p className="text-3xl font-bold text-consumer">{businesses.length}</p>
          </div>

          {coasterStats && (
            <div className={`rounded-2xl p-6 ${
              coasterStats.status === 'good' ? 'bg-green-50' :
              coasterStats.status === 'warning' ? 'bg-amber-50' : 'bg-red-50'
            }`}>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${
                coasterStats.status === 'good' ? 'bg-green-100' :
                coasterStats.status === 'warning' ? 'bg-amber-100' : 'bg-red-100'
              }`}>
                <Package size={20} className={
                  coasterStats.status === 'good' ? 'text-green-600' :
                  coasterStats.status === 'warning' ? 'text-amber-600' : 'text-red-600'
                } />
              </div>
              <p className="text-sm text-navy/50 mb-1">Coasters Left</p>
              <p className={`text-3xl font-bold ${
                coasterStats.status === 'good' ? 'text-green-600' :
                coasterStats.status === 'warning' ? 'text-amber-600' : 'text-red-600'
              }`}>
                {coasterStats.remaining.toLocaleString()}
              </p>
              <p className="text-xs text-navy/40 mt-1">{coasterStats.daysRemaining} days left</p>
            </div>
          )}

          <div className="bg-business/5 rounded-2xl p-6">
            <div className="w-10 h-10 bg-business/10 rounded-xl flex items-center justify-center mb-4">
              <Activity size={20} className="text-business" />
            </div>
            <p className="text-sm text-navy/50 mb-1">Status</p>
            <p className="text-xl font-bold text-venue">
              {venuePartner ? 'Venue Partner' : 'Hub Location'}
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="relative bg-white rounded-2xl shadow-sm border border-navy/5 overflow-hidden">
          {/* right-edge fade: the strip scrolls on a narrow screen with no other cue */}
          <div className="pointer-events-none absolute top-0 right-0 h-[57px] w-8 z-10 bg-gradient-to-l from-white sm:hidden" aria-hidden="true" />
          <nav className="flex -mb-px overflow-x-auto border-b border-navy/10">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 sm:px-5 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'border-venue text-venue'
                      : 'border-transparent text-navy/60 hover:text-navy hover:border-navy/20'
                  }`}
                >
                  <Icon size={16} />
                  {tab.label}
                  {tab.badge !== undefined && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      activeTab === tab.id ? 'bg-venue/20 text-venue' : 'bg-navy/10 text-navy/60'
                    }`}>
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Tab Content */}
          <div className="lg:col-span-2 space-y-6">

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <OverviewTab
                location={location}
                venuePartner={venuePartner}
                formatDateEST={formatDateEST}
                formatDateOnlyEST={formatDateOnlyEST}
              />
            )}

            {/* QR Code Tab */}
            {activeTab === 'qrcode' && (
              <QRCodeTab
                location={location}
                handleDownloadQR={handleDownloadQR}
                handleRegenerateQR={handleRegenerateQR}
                regeneratingQR={regeneratingQR}
              />
            )}

            {/* Venue Partner Tab */}
            {activeTab === 'venue' && venuePartner && (
              <VenuePartnerTab
                location={location}
                venuePartner={venuePartner}
                selectedDesign={selectedDesign}
                liveCoasterFront={liveCoasterFront}
                liveCoasterBack={liveCoasterBack}
                setLiveCoasterFront={setLiveCoasterFront}
                setLiveCoasterBack={setLiveCoasterBack}
                setAsDefault={setAsDefault}
                setSetAsDefault={setSetAsDefault}
                uploadingLiveCoaster={uploadingLiveCoaster}
                handleUploadLiveCoaster={handleUploadLiveCoaster}
                handleRemoveLiveCoaster={handleRemoveLiveCoaster}
                handleUploadLogo={handleUploadLogo}
                handleDeleteLogo={handleDeleteLogo}
                setShowCoasterModal={setShowCoasterModal}
              />
            )}

            {/* Coasters Tab */}
            {activeTab === 'coasters' && coasterStats && (
              <CoastersTab
                location={location}
                coasterStats={coasterStats}
                formatDateOnlyEST={formatDateOnlyEST}
              />
            )}

            {/* Advertisers Tab */}
            {activeTab === 'advertisers' && (
              <AdvertisersTab
                businesses={businesses}
                featuredBusinessId={featuredBusinessId}
                savingFeatured={savingFeatured}
                handleSetFeatured={handleSetFeatured}
              />
            )}
          </div>

          {/* Right Column - Persistent Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <Card>
              <CardContent className="p-4 sm:p-6">
                <h2 className="font-semibold text-navy mb-4">Quick Actions</h2>
                <div className="space-y-3">
                  <Button
                    className="w-full justify-start text-white"
                    style={{ backgroundColor: '#6F5BEB' }}
                    onClick={() => setShowEditModal(true)}
                  >
                    <Edit2 size={18} className="mr-2" />
                    Edit Location Details
                  </Button>

                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => navigate('/admin/businesses')}
                  >
                    <Store size={18} className="mr-2" />
                    View All Businesses
                  </Button>

                  {venuePartner && (
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => navigate('/admin/venue-applications')}
                    >
                      <Coffee size={18} className="mr-2" />
                      Manage Venue Partner
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Admin Notes */}
            <Card>
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-navy">Admin Notes</h2>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowNotesModal(true)}
                  >
                    <Edit2 size={14} className="mr-1" />
                    Edit
                  </Button>
                </div>
                {adminNotes ? (
                  <div className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded-lg border border-gray-200">
                    {adminNotes}
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-400 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <FileText size={32} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No notes yet</p>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowNotesModal(true)}
                      className="mt-2"
                    >
                      Add Note
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Metadata */}
            <Card className="bg-gray-50">
              <CardContent className="p-4 sm:p-6">
                <h2 className="font-semibold text-navy mb-4">Metadata</h2>
                <div className="space-y-3 text-xs sm:text-sm">
                  <div>
                    <p className="text-gray-600 mb-1">Location ID</p>
                    <p className="font-mono text-xs bg-white px-2 py-1 rounded border break-all">{location.id}</p>
                  </div>
                  {location.created_at && (
                    <div>
                      <p className="text-gray-600 mb-1">Created</p>
                      <p className="text-gray-900">{formatDateEST(location.created_at)}</p>
                    </div>
                  )}
                  {location.updated_at && (
                    <div>
                      <p className="text-gray-600 mb-1">Last Updated</p>
                      <p className="text-gray-900">{formatDateEST(location.updated_at)}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Admin Notes Modal */}
        {showNotesModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-display text-xl text-navy">Admin Notes</h3>
                <button
                  onClick={() => setShowNotesModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="p-6">
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add internal notes about this location..."
                  className="w-full h-64 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <div className="flex gap-3 mt-4">
                  <Button
                    onClick={handleSaveNotes}
                    style={{ backgroundColor: '#6F5BEB', color: 'white' }}
                  >
                    Save Notes
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowNotesModal(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Location Modal */}
        {showEditModal && (
          <LocationEditModal
            location={location}
            onClose={() => setShowEditModal(false)}
            onSuccess={() => {
              setShowEditModal(false);
              fetchLocationDetails();
              toast.success('Location updated successfully');
            }}
          />
        )}

        {/* Change Coaster Design Modal */}
        {showCoasterModal && (
          <CoasterDesignModal
            location={location}
            coasterDesigns={coasterDesigns}
            handleUpdateCoasterDesign={handleUpdateCoasterDesign}
            onClose={() => setShowCoasterModal(false)}
          />
        )}
      </div>
    </AdminLayout>
  );
};

// ============================================================================
// TAB COMPONENTS
// ============================================================================

// Overview Tab Component
const OverviewTab = ({ location, venuePartner, formatDateEST, formatDateOnlyEST }) => (
  <div className="space-y-6">
    {/* Basic Information */}
    <Card>
      <CardContent className="p-6">
        <div className="bg-gradient-to-r from-venue to-teal-400 -mx-6 -mt-6 px-6 py-4 mb-6 rounded-t-xl">
          <h2 className="font-display text-lg text-white flex items-center gap-2">
            <Building2 size={20} />
            Location Information
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-semibold text-gray-600 block mb-2">Address</label>
            <p className="text-gray-900">
              {location.address?.street || location.address?.formatted || location.address}
            </p>
            <p className="text-gray-900">
              {location.address?.city || location.city}, {location.address?.state || location.state} {location.address?.zip_code || location.zip_code}
            </p>
          </div>

          <div>
            <label className="text-sm font-semibold text-gray-600 block mb-2">Contact</label>
            {location.phone && (
              <p className="text-gray-900 flex items-center gap-2">
                <Phone size={16} />
                {location.phone}
              </p>
            )}
            {location.email && (
              <p className="text-gray-900 flex items-center gap-2">
                <Mail size={16} />
                {location.email}
              </p>
            )}
            {!location.phone && !location.email && (
              <p className="text-gray-400">No contact information</p>
            )}
          </div>

          {(location.contact_first_name || location.contact_last_name) && (
            <div>
              <label className="text-sm font-semibold text-gray-600 block mb-2">Contact Person</label>
              <p className="text-gray-900">
                {location.contact_first_name} {location.contact_last_name}
              </p>
            </div>
          )}

          <div>
            <label className="text-sm font-semibold text-gray-600 block mb-2">Slug</label>
            <p className="text-gray-900 font-mono text-sm bg-gray-100 px-3 py-2 rounded">
              {location.slug}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Activity Timeline */}
    <Card>
      <CardContent className="p-6">
        <h2 className="font-display text-lg text-navy flex items-center gap-2 mb-6">
          <Activity size={20} className="text-business" />
          Activity Timeline
        </h2>

        <div className="space-y-4">
          {location.created_at && (
            <div className="flex items-start gap-3 pb-4 border-b border-gray-200">
              <div className="w-2 h-2 rounded-full bg-purple-500 mt-2"></div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">Location Created</p>
                <p className="text-xs text-gray-500">{formatDateEST(location.created_at)}</p>
              </div>
            </div>
          )}

          {venuePartner?.approved_at && (
            <div className="flex items-start gap-3 pb-4 border-b border-gray-200">
              <div className="w-2 h-2 rounded-full bg-green-500 mt-2"></div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">Venue Partner Approved</p>
                <p className="text-xs text-gray-500">{formatDateEST(venuePartner.approved_at)}</p>
              </div>
            </div>
          )}

          {location.last_coaster_order_date && (
            <div className="flex items-start gap-3 pb-4 border-b border-gray-200">
              <div className="w-2 h-2 rounded-full bg-blue-500 mt-2"></div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">Coaster Order Placed</p>
                <p className="text-xs text-gray-500">{formatDateEST(location.last_coaster_order_date)}</p>
                {location.initial_coaster_quantity && (
                  <p className="text-xs text-gray-600 mt-1">{location.initial_coaster_quantity.toLocaleString()} coasters</p>
                )}
              </div>
            </div>
          )}

          {location.updated_at && location.updated_at !== location.created_at && (
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-gray-400 mt-2"></div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">Last Updated</p>
                <p className="text-xs text-gray-500">{formatDateEST(location.updated_at)}</p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  </div>
);

// QR Code Tab Component
const QRCodeTab = ({ location, handleDownloadQR, handleRegenerateQR, regeneratingQR }) => (
  <Card>
    <CardContent className="p-6">
      <div className="bg-gradient-to-r from-venue to-teal-400 -mx-6 -mt-6 px-6 py-4 mb-6 rounded-t-xl">
        <h2 className="font-display text-lg text-white flex items-center gap-2">
          <QrCode size={20} />
          Location QR Code
        </h2>
      </div>

      {location.qr_code_url ? (
        <div className="max-w-md mx-auto">
          <div className="bg-white border-4 border-purple-200 rounded-xl p-6 mb-6">
            <img
              src={`${resolveAssetUrl(location.qr_code_url)}?t=${Date.now()}`}
              alt="Location QR Code"
              className="w-full h-auto"
            />
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-purple-700 font-medium mb-1">Scans to:</p>
            <code className="text-sm font-mono text-purple-900 break-all">
              https://tappd.app/scan?hub_id={location.id}
            </code>
          </div>

          <div className="space-y-3">
            <Button
              onClick={handleDownloadQR}
              className="w-full"
              style={{ backgroundColor: '#6F5BEB', color: 'white' }}
            >
              <Download size={18} className="mr-2" />
              Download QR Code (2000x2000)
            </Button>

            <Button
              onClick={handleRegenerateQR}
              variant="outline"
              className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
              disabled={regeneratingQR}
            >
              <RefreshCw size={18} className={`mr-2 ${regeneratingQR ? 'animate-spin' : ''}`} />
              {regeneratingQR ? 'Regenerating...' : 'Regenerate QR Code'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <QrCode size={64} className="mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 mb-4">QR Code not generated yet</p>
          <Button
            onClick={handleRegenerateQR}
            disabled={regeneratingQR}
            style={{ backgroundColor: '#6F5BEB', color: 'white' }}
          >
            <RefreshCw size={18} className={`mr-2 ${regeneratingQR ? 'animate-spin' : ''}`} />
            {regeneratingQR ? 'Generating...' : 'Generate QR Code'}
          </Button>
        </div>
      )}
    </CardContent>
  </Card>
);

// Venue Partner Tab Component
const VenuePartnerTab = ({
  location,
  venuePartner,
  selectedDesign,
  liveCoasterFront,
  liveCoasterBack,
  setLiveCoasterFront,
  setLiveCoasterBack,
  setAsDefault,
  setSetAsDefault,
  uploadingLiveCoaster,
  handleUploadLiveCoaster,
  handleRemoveLiveCoaster,
  handleUploadLogo,
  handleDeleteLogo,
  setShowCoasterModal
}) => (
  <div className="space-y-6">
    {/* Venue Partner Info */}
    <Card className="border-2 border-purple-200">
      <CardContent className="p-6">
        <div className="bg-gradient-to-r from-purple-500 to-pink-400 -mx-6 -mt-6 px-6 py-4 mb-6 rounded-t-xl">
          <h2 className="font-display text-lg text-white flex items-center gap-2">
            <Coffee size={20} />
            Venue Partner Details
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="text-sm font-semibold text-gray-600 block mb-2">Venue Name</label>
            <p className="text-gray-900 font-semibold text-lg">{venuePartner.venue_name}</p>
          </div>

          <div>
            <label className="text-sm font-semibold text-gray-600 block mb-2">Status</label>
            <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full font-semibold ${
              venuePartner.status === 'approved' ? 'bg-green-100 text-green-800' :
              venuePartner.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
              'bg-red-100 text-red-800'
            }`}>
              {venuePartner.status === 'approved' && <CheckCircle size={16} />}
              {venuePartner.status === 'pending' && <AlertCircle size={16} />}
              {venuePartner.status.charAt(0).toUpperCase() + venuePartner.status.slice(1)}
            </span>
          </div>

          {venuePartner.daily_customer_estimate && (
            <div>
              <label className="text-sm font-semibold text-gray-600 block mb-2">Daily Customers</label>
              <p className="text-gray-900">{venuePartner.daily_customer_estimate} estimated</p>
            </div>
          )}

          {venuePartner.delivery_frequency && (
            <div>
              <label className="text-sm font-semibold text-gray-600 block mb-2">Delivery Frequency</label>
              <p className="text-gray-900 capitalize">{venuePartner.delivery_frequency}</p>
            </div>
          )}
        </div>

        {/* Logo Management */}
        <div className="border-t border-purple-200 pt-6">
          <label className="text-sm font-semibold text-gray-600 block mb-3">Venue Logo</label>
          {venuePartner.logo_url ? (
            <div className="flex items-start gap-4">
              <div className="w-32 h-32 bg-white rounded-lg border-2 border-purple-200 p-2 flex items-center justify-center">
                <img
                  src={resolveAssetUrl(venuePartner.logo_url)}
                  alt="Venue Logo"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = resolveAssetUrl(venuePartner.logo_url);
                    link.download = 'venue-logo.png';
                    link.click();
                  }}
                >
                  <Download size={16} className="mr-1" />
                  Download
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => document.getElementById('logo-upload-input').click()}
                  className="text-purple-600 hover:text-purple-700"
                >
                  <Upload size={16} className="mr-1" />
                  Replace
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDeleteLogo()}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 size={16} className="mr-1" />
                  Delete
                </Button>
              </div>
              <input
                id="logo-upload-input"
                type="file"
                accept="image/*"
                onChange={(e) => handleUploadLogo(e.target.files[0])}
                className="hidden"
              />
            </div>
          ) : (
            <div>
              <div className="w-32 h-32 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center mb-3">
                <div className="text-center text-gray-400">
                  <ImageIcon size={24} className="mx-auto mb-1" />
                  <p className="text-xs">No logo uploaded</p>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => document.getElementById('logo-upload-input-empty').click()}
                className="text-purple-600 hover:text-purple-700"
              >
                <Upload size={16} className="mr-1" />
                Upload Logo
              </Button>
              <input
                id="logo-upload-input-empty"
                type="file"
                accept="image/*"
                onChange={(e) => handleUploadLogo(e.target.files[0])}
                className="hidden"
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>

    {/* Coaster Design Selection */}
    <Card className="border-2 border-purple-200">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Selected Coaster Design</h3>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowCoasterModal(true)}
            className="text-purple-600 hover:text-purple-700"
          >
            <Edit2 size={14} className="mr-1" />
            Change
          </Button>
        </div>
        {selectedDesign ? (
          <div className="flex items-center gap-4">
            <div className="w-32 h-32 bg-white rounded-lg border-2 border-purple-200 p-2 flex items-center justify-center">
              <img
                src={resolveAssetUrl(selectedDesign.image_url)}
                alt={selectedDesign.name}
                className="max-w-full max-h-full object-contain rounded"
              />
            </div>
            <div>
              <p className="font-semibold text-gray-900">{selectedDesign.name}</p>
              <p className="text-sm text-gray-600 capitalize">{selectedDesign.season} {selectedDesign.year}</p>
            </div>
          </div>
        ) : (
          <div className="w-32 h-32 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
            <div className="text-center text-gray-400">
              <Package size={24} className="mx-auto mb-1" />
              <p className="text-xs">No design selected</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>

    {/* Live Coaster Design */}
    <Card className="border-2 border-green-200">
      <CardContent className="p-6">
        <div className="bg-gradient-to-r from-green-500 to-emerald-400 -mx-6 -mt-6 px-6 py-4 mb-6 rounded-t-xl">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg text-white flex items-center gap-2">
              <ImageIcon size={20} />
              Live Coaster Design
            </h2>
            <span className="px-2 py-0.5 bg-white/20 text-white text-xs rounded-full">MASTER ADMIN</span>
          </div>
          <p className="text-white/80 text-sm mt-1">This is what appears on the Scan page</p>
        </div>

        {/* Current Live Coaster Display */}
        {location?.live_coaster_front_url ? (
          <div className="mb-6">
            <div className="flex gap-4 mb-4">
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-2 text-center font-medium">FRONT</p>
                <div className="w-full aspect-square max-w-[160px] mx-auto bg-white rounded-lg border-2 border-green-300 p-2 flex items-center justify-center">
                  <img
                    src={resolveAssetUrl(location.live_coaster_front_url)}
                    alt="Live Coaster Front"
                    className="max-w-full max-h-full object-contain rounded"
                  />
                </div>
              </div>
              {location?.live_coaster_back_url && (
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-2 text-center font-medium">BACK</p>
                  <div className="w-full aspect-square max-w-[160px] mx-auto bg-white rounded-lg border-2 border-green-300 p-2 flex items-center justify-center">
                    <img
                      src={resolveAssetUrl(location.live_coaster_back_url)}
                      alt="Live Coaster Back"
                      className="max-w-full max-h-full object-contain rounded"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-green-600 flex items-center gap-1">
                <CheckCircle size={12} />
                Live coaster active
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={handleRemoveLiveCoaster}
                className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
              >
                <Trash2 size={14} className="mr-1" />
                Remove
              </Button>
            </div>
          </div>
        ) : (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-dashed border-gray-300">
            <p className="text-sm text-gray-500 text-center">
              No live coaster uploaded. Using system default.
            </p>
          </div>
        )}

        {/* Upload New Live Coaster */}
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <h4 className="text-sm font-semibold text-green-900 mb-4 flex items-center gap-2">
            <Upload size={16} />
            Upload Live Coaster
          </h4>

          <div className="grid grid-cols-2 gap-4 mb-4">
            {/* Front Image Upload */}
            <div>
              <p className="text-xs text-gray-600 mb-2 font-medium">FRONT (Required for Scan)</p>
              <label className="block cursor-pointer">
                <div className={`w-full aspect-square border-2 border-dashed rounded-lg flex flex-col items-center justify-center transition-colors ${
                  liveCoasterFront ? 'border-green-400 bg-green-50' : 'border-green-300 hover:border-green-400 bg-white'
                }`}>
                  {liveCoasterFront ? (
                    <div className="text-center p-2">
                      <CheckCircle size={24} className="mx-auto mb-1 text-green-600" />
                      <p className="text-xs text-green-700 truncate max-w-full px-2">{liveCoasterFront.name}</p>
                    </div>
                  ) : (
                    <div className="text-center p-2">
                      <ImageIcon size={24} className="mx-auto mb-1 text-green-400" />
                      <p className="text-xs text-green-600">Click to upload</p>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => setLiveCoasterFront(e.target.files[0])}
                />
              </label>
            </div>

            {/* Back Image Upload */}
            <div>
              <p className="text-xs text-gray-600 mb-2 font-medium">BACK (Optional)</p>
              <label className="block cursor-pointer">
                <div className={`w-full aspect-square border-2 border-dashed rounded-lg flex flex-col items-center justify-center transition-colors ${
                  liveCoasterBack ? 'border-green-400 bg-green-50' : 'border-green-300 hover:border-green-400 bg-white'
                }`}>
                  {liveCoasterBack ? (
                    <div className="text-center p-2">
                      <CheckCircle size={24} className="mx-auto mb-1 text-green-600" />
                      <p className="text-xs text-green-700 truncate max-w-full px-2">{liveCoasterBack.name}</p>
                    </div>
                  ) : (
                    <div className="text-center p-2">
                      <ImageIcon size={24} className="mx-auto mb-1 text-green-400" />
                      <p className="text-xs text-green-600">Click to upload</p>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => setLiveCoasterBack(e.target.files[0])}
                />
              </label>
            </div>
          </div>

          {/* Set as Default Checkbox */}
          <label className="flex items-center gap-2 mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={setAsDefault}
              onChange={(e) => setSetAsDefault(e.target.checked)}
              className="rounded border-green-300 text-green-600 focus:ring-green-500"
            />
            <span className="text-sm text-gray-700">Set as system-wide DEFAULT coaster</span>
          </label>

          {/* Upload Button */}
          <Button
            onClick={handleUploadLiveCoaster}
            disabled={uploadingLiveCoaster || (!liveCoasterFront && !liveCoasterBack)}
            className="w-full"
            style={{ backgroundColor: '#22c55e' }}
          >
            {uploadingLiveCoaster ? (
              <>
                <RefreshCw size={16} className="mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload size={16} className="mr-2" />
                Upload Live Coaster
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  </div>
);

// Coasters Tab Component
const CoastersTab = ({ location, coasterStats, formatDateOnlyEST }) => !flag('COASTER_INVENTORY') ? (
  <Card className="border-2 border-purple-200">
    <CardContent className="p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Coaster inventory tracking is paused</h3>
      <p className="text-sm text-gray-600">
        Phase 1 removed the simulated inventory counter (it was an estimate, not a count). Venues request more
        coasters by email; turn on the COASTER_INVENTORY flag to bring this tab back.
      </p>
    </CardContent>
  </Card>
) : (
  <Card className="border-2 border-purple-200">
    <CardContent className="p-6">
      <div className="bg-gradient-to-r from-amber-500 to-orange-400 -mx-6 -mt-6 px-6 py-4 mb-6 rounded-t-xl">
        <h2 className="font-display text-lg text-white flex items-center gap-2">
          <Package size={20} />
          Coaster Inventory Management
        </h2>
      </div>

      <div className="grid sm:grid-cols-2 gap-6 mb-6">
        <div>
          <label className="text-sm font-semibold text-gray-600 block mb-2">Current Inventory</label>
          <div className="flex items-baseline gap-2">
            <p className="text-4xl font-bold" style={{
              color: coasterStats.status === 'good' ? '#10B981' :
                     coasterStats.status === 'warning' ? '#F59E0B' : '#EF4444'
            }}>
              {coasterStats.remaining.toLocaleString()}
            </p>
            <span className="text-gray-500">coasters</span>
          </div>
          <div className="mt-3 bg-gray-200 rounded-full h-3 overflow-hidden">
            <div
              className="h-full transition-all"
              style={{
                width: `${coasterStats.percentage}%`,
                backgroundColor: coasterStats.status === 'good' ? '#10B981' :
                               coasterStats.status === 'warning' ? '#F59E0B' : '#EF4444'
              }}
            />
          </div>
          <p className="text-sm text-gray-500 mt-2">
            {coasterStats.percentage.toFixed(1)}% of initial inventory remaining
          </p>
        </div>

        <div>
          <label className="text-sm font-semibold text-gray-600 block mb-2">Daily Usage</label>
          <div className="flex items-baseline gap-2">
            <p className="text-4xl font-bold text-gray-900">
              {coasterStats.dailyUsage}
            </p>
            <span className="text-gray-500">per day</span>
          </div>
          <p className="text-sm text-gray-600 mt-3">
            Projected depletion in{' '}
            <span className="font-bold" style={{ color: coasterStats.status === 'critical' ? '#EF4444' : '#6F5BEB' }}>
              {coasterStats.daysRemaining} days
            </span>
          </p>
        </div>

        {location.last_coaster_order_date && (
          <div>
            <label className="text-sm font-semibold text-gray-600 block mb-2">Last Order</label>
            <p className="text-gray-900 flex items-center gap-2">
              <Calendar size={16} />
              {formatDateOnlyEST(location.last_coaster_order_date)}
            </p>
            {location.initial_coaster_quantity && (
              <p className="text-sm text-gray-500 mt-1">
                {location.initial_coaster_quantity.toLocaleString()} coasters delivered
              </p>
            )}
          </div>
        )}

        {location.delivery_frequency && (
          <div>
            <label className="text-sm font-semibold text-gray-600 block mb-2">Delivery Frequency</label>
            <p className="text-gray-900 capitalize flex items-center gap-2">
              <Truck size={16} />
              {location.delivery_frequency}
            </p>
          </div>
        )}
      </div>

      {coasterStats.status === 'critical' && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle size={18} />
            <span className="font-semibold">Low Inventory Alert!</span>
          </div>
          <p className="text-sm text-red-700 mt-1">
            Coaster supply is critically low. Consider ordering a refill soon.
          </p>
        </div>
      )}

      {coasterStats.status === 'warning' && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center gap-2 text-amber-800">
            <AlertCircle size={18} />
            <span className="font-semibold">Inventory Running Low</span>
          </div>
          <p className="text-sm text-amber-700 mt-1">
            Consider placing a coaster order soon to avoid running out.
          </p>
        </div>
      )}
    </CardContent>
  </Card>
);

// Advertisers Tab Component
const AdvertisersTab = ({ businesses, featuredBusinessId, savingFeatured, handleSetFeatured }) => (
  <Card>
    <CardContent className="p-6">
      <div className="bg-gradient-to-r from-consumer to-red-400 -mx-6 -mt-6 px-6 py-4 mb-6 rounded-t-xl">
        <h2 className="font-display text-lg text-white flex items-center gap-2">
          <Store size={20} />
          Advertisers ({businesses.length})
        </h2>
      </div>

      {/* Featured Business Info */}
      <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-center gap-2 text-amber-800">
          <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
          <span className="text-sm font-medium">
            {featuredBusinessId
              ? `Featured: ${businesses.find(b => b.id === featuredBusinessId)?.name || 'Unknown'}`
              : 'No featured business set'
            }
          </span>
        </div>
        <p className="text-xs text-amber-600 mt-1">
          The featured business appears at the top of the Deals page when this location is selected.
        </p>
      </div>

      {businesses.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Store size={48} className="mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">No businesses attached to this location yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {businesses.map((business) => (
            <div
              key={business.id}
              className={`border rounded-lg p-4 transition-all ${
                featuredBusinessId === business.id
                  ? 'bg-amber-50 border-amber-300'
                  : 'border-gray-200 hover:border-purple-300 hover:shadow-md'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-lg font-bold">{business.name}</h3>
                    {featuredBusinessId === business.id && (
                      <span className="px-2 py-0.5 bg-amber-500 text-white text-xs font-semibold rounded-full flex items-center gap-1">
                        <Star className="w-3 h-3 fill-white" />
                        Featured
                      </span>
                    )}
                  </div>
                  {business.category && (
                    <p className="text-sm text-gray-500 capitalize mb-2">{business.category}</p>
                  )}
                  {business.address && (
                    <p className="text-sm text-gray-600 flex items-center gap-1">
                      <MapPin size={14} />
                      {typeof business.address === 'string'
                        ? business.address
                        : business.address.formatted ||
                          `${business.address.street || ''} ${business.address.city || ''}, ${business.address.state || ''} ${business.address.zip_code || ''}`.trim()
                      }
                    </p>
                  )}
                  {business.active_deals_count !== undefined && (
                    <p className="text-sm text-purple-600 font-semibold mt-2">
                      {business.active_deals_count} Active Deal{business.active_deals_count !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
                  <Button
                    variant={featuredBusinessId === business.id ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleSetFeatured(business.id)}
                    disabled={savingFeatured}
                    className={`min-w-[100px] ${
                      featuredBusinessId === business.id
                        ? 'bg-amber-500 hover:bg-amber-600 text-white border-amber-500'
                        : 'hover:bg-amber-50 hover:text-amber-700 hover:border-amber-300'
                    }`}
                  >
                    {featuredBusinessId === business.id ? (
                      <>
                        <Star className="w-4 h-4 mr-1 fill-white" />
                        Featured
                      </>
                    ) : (
                      <>
                        <StarOff className="w-4 h-4 mr-1" />
                        Set Featured
                      </>
                    )}
                  </Button>
                  {business.slug && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`/business/${business.slug}`, '_blank')}
                    >
                      <ExternalLink size={16} className="mr-1" />
                      View
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

// ============================================================================
// MODAL COMPONENTS
// ============================================================================

// Location Edit Modal Component
const LocationEditModal = ({ location, onClose, onSuccess }) => {
  const getAddressField = (field) => {
    if (location?.address && typeof location.address === 'object') {
      return location.address[field] || '';
    }
    return location?.[field] || '';
  };

  const [formData, setFormData] = useState({
    name: location?.name || '',
    slug: location?.slug || '',
    address: getAddressField('street') || getAddressField('formatted') || (typeof location?.address === 'string' ? location?.address : ''),
    city: getAddressField('city') || location?.city || '',
    state: getAddressField('state') || location?.state || '',
    zip_code: getAddressField('zip_code') || location?.zip_code || '',
    phone: location?.phone || '',
    email: location?.email || '',
    contact_first_name: location?.contact_first_name || '',
    contact_last_name: location?.contact_last_name || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await adminApi.put(
        `/admin/locations/${location.id}`,
        formData
      );
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl my-8">
        <div className="p-6 sm:p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-xl text-navy">Edit Location</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">Location Name *</label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="rounded-xl"
                placeholder="e.g., Ott's Medford"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">Slug * (URL-friendly identifier)</label>
              <Input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                required
                className="rounded-xl"
                placeholder="e.g., otts-medford"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">Street Address *</label>
              <Input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                required
                className="rounded-xl"
                placeholder="123 Main Street"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-semibold mb-2 text-gray-700">City *</label>
                <Input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required
                  className="rounded-xl"
                  placeholder="Medford"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">State *</label>
                <Input
                  type="text"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value.toUpperCase() })}
                  required
                  className="rounded-xl"
                  placeholder="NJ"
                  maxLength="2"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">ZIP Code *</label>
              <Input
                type="text"
                value={formData.zip_code}
                onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                required
                className="rounded-xl"
                placeholder="08055"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">Phone Number</label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="rounded-xl"
                placeholder="(609) 555-0100"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">Email</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="rounded-xl"
                placeholder="contact@location.com"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Contact First Name</label>
                <Input
                  type="text"
                  value={formData.contact_first_name}
                  onChange={(e) => setFormData({ ...formData, contact_first_name: e.target.value })}
                  className="rounded-xl"
                  placeholder="John"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Contact Last Name</label>
                <Input
                  type="text"
                  value={formData.contact_last_name}
                  onChange={(e) => setFormData({ ...formData, contact_last_name: e.target.value })}
                  className="rounded-xl"
                  placeholder="Doe"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                disabled={loading}
                className="flex-1"
                style={{ backgroundColor: '#6F5BEB', color: 'white' }}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Coaster Design Modal Component
const CoasterDesignModal = ({ location, coasterDesigns, handleUpdateCoasterDesign, onClose }) => (
  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 bg-white">
        <h2 className="font-display text-xl text-navy">Change Coaster Design</h2>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <X size={24} />
        </button>
      </div>

      <div className="px-6 py-6">
        <p className="text-gray-600 mb-6">
          Select a coaster design for this location. Only active designs are visible to venue partners.
        </p>

        <div className="grid md:grid-cols-3 gap-6">
          {coasterDesigns.map((design) => (
            <div
              key={design.id}
              className={`cursor-pointer border-2 rounded-xl overflow-hidden transition-all hover:shadow-lg ${
                location?.selected_coaster_id === design.id
                  ? 'border-purple-500 ring-4 ring-purple-200'
                  : 'border-gray-200 hover:border-purple-300'
              }`}
              onClick={() => handleUpdateCoasterDesign(design.id)}
            >
              <div className="aspect-square bg-gray-100 flex items-center justify-center p-4">
                <img
                  src={resolveAssetUrl(design.image_url)}
                  alt={design.name}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-gray-900">{design.name}</h3>
                  {design.is_active ? (
                    <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded-full">
                      Active
                    </span>
                  ) : (
                    <span className="text-xs font-semibold text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                      Inactive
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 capitalize">{design.season} {design.year}</p>
                {location?.selected_coaster_id === design.id && (
                  <div className="mt-3 flex items-center gap-2 text-purple-600 font-semibold text-sm">
                    <CheckCircle size={16} />
                    <span>Currently Selected</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {coasterDesigns.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Package size={48} className="mx-auto mb-4 text-gray-400" />
            <p>No coaster designs available</p>
          </div>
        )}
      </div>
    </div>
  </div>
);

export default LocationDetail;
